import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Plus, Search } from "lucide-react";

// Design: components/SearchSelect.tsx

/** Lists with more than this many options get a search box. */
export const SEARCH_THRESHOLD = 5;

export const toOptions = (values) => values.map((v) => ({ value: v, label: v }));

/**
 * Form dropdown. Replaces native <select> wherever data is dynamic or can grow past 5 items.
 * Search appears automatically above SEARCH_THRESHOLD options (or force with `searchable`).
 * `onCreate` adds a "Add “query”" row when nothing matches exactly.
 * Options: strings, or { value, label, hint?, leading?, disabled? }.
 */
export function SearchSelect({
  id,
  value,
  options,
  onChange,
  placeholder = "Choose",
  searchPlaceholder = "Search",
  searchable,
  invalid,
  disabled,
  size = "md",
  onCreate,
  createLabel = "Add",
  emptyText = "No matches. Try a different word.",
  className = "",
  ariaLabel,
}) {
  const opts = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options],
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrap = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  // Popover is portalled to <body> with fixed position so tables and cards with overflow never clip it.
  const [pos, setPos] = useState(null);
  const listId = useId();
  const showSearch = searchable ?? (opts.length > SEARCH_THRESHOLD || !!onCreate);
  const selected = opts.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) => `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(q));
  }, [opts, query]);
  const canCreate = !!onCreate && query.trim() !== "" && !opts.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());
  const rowCount = filtered.length + (canCreate ? 1 : 0);

  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => {
      const t = e.target;
      if (wrap.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    };
    document.addEventListener("mousedown", down);
    const idx = Math.max(0, filtered.findIndex((o) => o.value === value));
    setActive(idx);
    requestAnimationFrame(() => {
      if (showSearch) searchRef.current?.focus();
      else listRef.current?.focus();
    });
    return () => document.removeEventListener("mousedown", down);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => setActive(0), [query]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.floor(Math.max(r.width, 224));
      const left = Math.floor(Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom;
      if (below < 300 && r.top > below) setPos({ left, width, bottom: Math.floor(window.innerHeight - r.top + 6) });
      else setPos({ left, width, top: Math.floor(r.bottom + 6) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-row="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const pick = (index) => {
    if (index < filtered.length) {
      const o = filtered[index];
      if (o.disabled) return;
      onChange(o.value);
    } else if (canCreate) {
      onCreate?.(query.trim());
    }
    close();
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(rowCount - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rowCount) pick(active);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      close();
    }
  };

  const height = size === "sm" ? "h-9" : "h-11";

  return (
    <div ref={wrap} className={`relative ${className}`}>
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- kept from the design: marks the field invalid for screen readers */}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`flex ${height} w-full min-w-0 items-center gap-2 rounded-sm border bg-paper px-3.5 text-left text-small outline-none disabled:cursor-not-allowed disabled:bg-mist disabled:text-muted ${
          invalid ? "border-danger" : open ? "border-ink ring-2 ring-ink/10" : "border-line hover:border-muted/50 focus-visible:border-ink focus-visible:ring-2 focus-visible:ring-ink/10"
        }`}
      >
        {selected?.leading}
        <span className={`min-w-0 flex-1 truncate ${selected ? "text-ink" : "text-muted/70"}`}>{selected ? selected.label : placeholder}</span>
        {selected?.hint && <span className="hidden shrink-0 truncate text-caption text-muted sm:inline">{selected.hint}</span>}
        <ChevronDown size={16} strokeWidth={1.75} className={`shrink-0 text-muted transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`} />
      </button>

      {open && pos && createPortal(
        <div ref={panelRef} style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }} className="olli fixed z-[70] animate-nav-in rounded-md bg-paper text-ink shadow-md ring-1 ring-line" onKeyDown={onKey} onMouseDown={(e) => e.stopPropagation()}>
          {showSearch && (
            <label className="flex h-10 items-center gap-2 border-b border-line px-3">
              <Search size={15} strokeWidth={1.75} className="shrink-0 text-muted" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-activedescendant={rowCount ? `${listId}-${active}` : undefined}
                className="h-full min-w-0 flex-1 bg-transparent text-small outline-none placeholder:text-muted"
              />
            </label>
          )}
          <ul id={listId} ref={listRef} role="listbox" tabIndex={-1} className="max-h-64 overflow-y-auto p-1.5 outline-none">
            {filtered.map((o, i) => (
              <li key={o.value} id={`${listId}-${i}`} data-row={i} role="option" aria-selected={o.value === value} aria-disabled={o.disabled || undefined}>
                <button
                  type="button"
                  tabIndex={-1}
                  disabled={o.disabled}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(i)}
                  className={`flex min-h-9 w-full items-center gap-2 rounded-xs px-3 py-1.5 text-left text-small disabled:cursor-not-allowed disabled:text-muted ${
                    i === active ? "bg-mist" : ""
                  } ${o.value === value ? "font-semibold" : ""}`}
                >
                  {o.leading}
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.hint && <span className="shrink-0 text-caption font-normal text-muted">{o.hint}</span>}
                  {o.value === value && <Check size={14} strokeWidth={2} className="shrink-0" />}
                </button>
              </li>
            ))}
            {canCreate && (
              <li id={`${listId}-${filtered.length}`} data-row={filtered.length} role="option" aria-selected={false}>
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseEnter={() => setActive(filtered.length)}
                  onClick={() => pick(filtered.length)}
                  className={`flex h-9 w-full items-center gap-2 rounded-xs px-3 text-left text-small font-medium text-sky-deep ${active === filtered.length ? "bg-mist" : ""}`}
                >
                  <Plus size={14} strokeWidth={2} />
                  {createLabel} “{query.trim()}”
                </button>
              </li>
            )}
            {!rowCount && <li className="px-3 py-3 text-small text-muted">{emptyText}</li>}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
